import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtIDR, fmtDate } from "@/lib_app/api";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  Wallet, AlertTriangle, ReceiptText, Coins, Layers, FileWarning,
  Building2, Percent, ChevronRight, ArrowRight, Users, PiggyBank,
} from "lucide-react";

const MONTH_LABEL = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const compact = (n) => {
  const v = Number(n || 0);
  if (v >= 1e9) return `${(v / 1e9).toFixed(1).replace(".", ",")} M`;
  if (v >= 1e6) return `${Math.round(v / 1e6)} jt`;
  if (v >= 1e3) return `${Math.round(v / 1e3)} rb`;
  return String(v);
};

export default function FinanceDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/analytics/finance-summary")
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, []);

  const monthTitle = useMemo(() => {
    const d = new Date();
    return `${MONTH_LABEL[d.getMonth()]} ${d.getFullYear()}`;
  }, []);

  if (err) return <div className="text-sm text-[#CE6968]" data-testid="finance-dash-error">Gagal memuat data keuangan.</div>;
  if (!data) return <div className="text-[#A0A3A8] text-sm">Memuat data tagihan...</div>;

  const k = data.kpi || {};
  const trend = (data.trend || []).map((t) => ({
    month: MONTH_LABEL[Number((t.month || "").split("-")[1]) - 1] || t.month,
    ditagih: t.billed,
    diterima: t.collected,
  }));
  const agingMax = Math.max(1, ...(data.aging || []).map((a) => a.amount));
  const collectRate = k.total_billed > 0 ? (k.total_collected * 100) / k.total_billed : 0;
  const ox = data.owner_extras;

  return (
    <div className="space-y-6 pb-16" data-testid="finance-dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.24em] text-[#626061]/70 mb-1.5 font-bold">Finance · {monthTitle}</p>
          <h1 className="text-[40px] md:text-[44px] tracking-tight font-bold text-[#090808] leading-none">
            Halo, {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="text-sm text-[#626061]/70 mt-2.5">Pusat kendali tagihan — piutang, jatuh tempo, dan penerimaan.</p>
        </div>
        <button
          onClick={() => navigate("/invoices")}
          data-testid="finance-goto-invoices"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#090808] text-white text-sm font-semibold hover:bg-black transition-all shadow-[0_8px_20px_rgba(9,8,8,0.18)]"
        >
          <ReceiptText className="w-4 h-4" /> Kelola Invoice <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <Kpi
          testid="finance-kpi-outstanding" tone="dark" icon={Wallet}
          label="Total Piutang" value={fmtIDR(k.outstanding)}
          sub={`${k.open_count || 0} invoice belum lunas`}
          onClick={() => navigate("/invoices?status=unpaid")}
        />
        <Kpi
          testid="finance-kpi-overdue" tone={k.overdue_count ? "red" : "plain"} icon={AlertTriangle}
          label="Lewat Jatuh Tempo" value={fmtIDR(k.overdue_amount)}
          sub={k.overdue_count ? `${k.overdue_count} invoice terlambat` : "Semua tepat waktu"}
          onClick={() => navigate("/invoices?aging=overdue")}
        />
        <Kpi
          testid="finance-kpi-issued" tone="plain" icon={ReceiptText}
          label="Tagihan Bulan Ini" value={fmtIDR(k.issued_this_month)}
          sub={`${k.issued_this_month_count || 0} invoice terbit`}
        />
        <Kpi
          testid="finance-kpi-collected" tone="green" icon={Coins}
          label="Penerimaan Bulan Ini" value={fmtIDR(k.collected_this_month)}
          sub={`Tingkat tertagih ${collectRate.toFixed(1).replace(".", ",")}%`}
        />
      </div>

      {/* Aging + Trend */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,420px)_1fr] gap-4">
        <Card className="p-6 border-0 rounded-3xl glass" data-testid="finance-aging-card">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-9 h-9 rounded-2xl bg-[#E5EEFA] text-[#3B4A63] flex items-center justify-center">
              <Layers className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div>
              <h3 className="text-[17px] font-bold text-[#090808]">Umur Piutang</h3>
              <p className="text-[11px] text-[#626061]/60 font-medium">Berdasarkan hari lewat jatuh tempo</p>
            </div>
          </div>
          <div className="space-y-2.5">
            {(data.aging || []).map((a) => {
              const pct = Math.round((a.amount / agingMax) * 100);
              const hot = a.key === "d61_90" || a.key === "d90p";
              const warm = a.key === "d1_30" || a.key === "d31_60";
              return (
                <button
                  key={a.key}
                  onClick={() => navigate(`/invoices?aging=${a.key}`)}
                  data-testid={`finance-aging-${a.key}`}
                  className="w-full text-left group"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-xs font-bold text-[#090808] group-hover:underline">{a.label}</span>
                    <span className="text-xs font-num tabular-nums font-bold text-[#090808]">{fmtIDR(a.amount)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex-1 h-2.5 rounded-full bg-black/5 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${hot ? "bg-[#CE6968]" : warm ? "bg-[#FFDB52]" : "bg-[#97ADD4]"}`}
                        style={{ width: `${Math.max(a.amount > 0 ? 6 : 0, pct)}%` }}
                      />
                    </div>
                    <span className="text-[10px] font-bold text-[#A0A3A8] w-16 text-right">{a.count} inv</span>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        <Card className="p-6 border-0 rounded-3xl glass" data-testid="finance-trend-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-[17px] font-bold text-[#090808]">Ditagih vs Diterima</h3>
              <p className="text-[11px] text-[#626061]/60 font-medium">12 bulan terakhir</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={trend} margin={{ top: 4, right: 8, left: 8, bottom: 0 }} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8A8D96" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={compact} tick={{ fontSize: 10, fill: "#A0A3A8" }} axisLine={false} tickLine={false} width={54} />
              <Tooltip
                formatter={(v, n) => [fmtIDR(v), n]}
                contentStyle={{ borderRadius: 14, border: "1px solid rgba(0,0,0,0.06)", fontSize: 12 }}
              />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 11, paddingTop: 6 }} formatter={(value) => <span style={{ color: "#626061", fontWeight: 600 }}>{value}</span>} />
              <Bar dataKey="ditagih" name="Ditagih" fill="#3B4A63" radius={[6, 6, 0, 0]} maxBarSize={18} />
              <Bar dataKey="diterima" name="Diterima" fill="#57D28A" radius={[6, 6, 0, 0]} maxBarSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Perlu tindakan */}
      <Card className="p-6 border-0 rounded-3xl glass" data-testid="finance-action-card">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-2xl bg-[#FFE89A] text-[#7A6633] flex items-center justify-center">
            <AlertTriangle className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div>
            <h3 className="text-[17px] font-bold text-[#090808]">Perlu Tindakan</h3>
            <span className="text-[11px] text-[#626061]/60 font-medium">
              {(data.action_needed || []).length} invoice · lewat jatuh tempo atau jatuh tempo ≤ 7 hari
            </span>
          </div>
        </div>
        {(data.action_needed || []).length === 0 ? (
          <div className="text-sm text-[#A0A3A8]">Tidak ada tagihan yang mendesak. 🎉</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {data.action_needed.map((r) => (
              <button
                key={r.id}
                onClick={() => navigate(`/invoices?open=${r.id}`)}
                data-testid={`finance-action-${r.id}`}
                className="w-full text-left bg-white/70 hover:bg-white rounded-2xl px-4 py-3 border border-white/70 transition-all flex items-center gap-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-num font-bold text-[#090808] tabular-nums">{r.number}</span>
                    <span
                      className={`inline-flex items-center h-5 px-2 rounded-full text-[10px] font-bold ${
                        r.days_late > 0 ? "bg-[#FDE7E9] text-[#CE6968]" : "bg-[#E5EEFA] text-[#3B4A63]"
                      }`}
                    >
                      {r.days_late > 0 ? `Telat ${r.days_late} hari` : r.days_late === 0 ? "Jatuh tempo hari ini" : `H-${Math.abs(r.days_late)}`}
                    </span>
                  </div>
                  <div className="text-sm font-bold text-[#090808] truncate mt-0.5">{r.customer}</div>
                  <div className="text-[11px] text-[#A0A3A8] font-semibold">Tempo {fmtDate(r.due_date)}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-num font-bold text-[#CE6968] tabular-nums">{fmtIDR(r.outstanding)}</div>
                  <div className="text-[10px] text-[#A0A3A8] font-num tabular-nums">dari {fmtIDR(r.grand_total)}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#A0A3A8] shrink-0" />
              </button>
            ))}
          </div>
        )}
      </Card>

      {ox && <OwnerFinanceBlocks ox={ox} navigate={navigate} />}

      {/* Draft + Top customers */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-6 border-0 rounded-3xl glass" data-testid="finance-draft-card">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-2xl bg-[#F4F5F7] text-[#626061] flex items-center justify-center">
              <FileWarning className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div>
              <h3 className="text-[17px] font-bold text-[#090808]">Draft Belum Diterbitkan</h3>
              <span className="text-[11px] text-[#626061]/60 font-medium">{(data.drafts || []).length} draft tertahan</span>
            </div>
          </div>
          {(data.drafts || []).length === 0 ? (
            <div className="text-sm text-[#A0A3A8]">Tidak ada draft tertahan.</div>
          ) : (
            <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
              {data.drafts.map((d) => (
                <button
                  key={d.id}
                  onClick={() => navigate(`/invoices?open=${d.id}`)}
                  data-testid={`finance-draft-${d.id}`}
                  className="w-full text-left bg-white/70 hover:bg-white rounded-2xl px-4 py-2.5 border border-white/70 transition-all flex items-center gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-[#090808] truncate">{d.customer}</div>
                    <div className="text-[11px] text-[#A0A3A8] font-semibold font-num tabular-nums">
                      {d.number} · {d.date ? fmtDate(d.date) : "-"}
                    </div>
                  </div>
                  <div className="text-sm font-num font-bold text-[#090808] tabular-nums shrink-0">{fmtIDR(d.grand_total)}</div>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6 border-0 rounded-3xl glass" data-testid="finance-topcust-card">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-2xl bg-[#E5EEFA] text-[#3B4A63] flex items-center justify-center">
              <Building2 className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div>
              <h3 className="text-[17px] font-bold text-[#090808]">Piutang Terbesar</h3>
              <span className="text-[11px] text-[#626061]/60 font-medium">Top 5 pelanggan</span>
            </div>
          </div>
          {(data.top_customers || []).length === 0 ? (
            <div className="text-sm text-[#A0A3A8]">Belum ada piutang berjalan.</div>
          ) : (
            <div className="space-y-2.5">
              {data.top_customers.map((c, i) => {
                const top = data.top_customers[0].outstanding || 1;
                return (
                  <div key={c.customer} data-testid={`finance-topcust-${i}`}>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-xs font-bold text-[#090808] truncate">{i + 1}. {c.customer}</span>
                      <span className="text-xs font-num tabular-nums font-bold text-[#090808]">{fmtIDR(c.outstanding)}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <div className="flex-1 h-2.5 rounded-full bg-black/5 overflow-hidden">
                        <div className="h-full rounded-full bg-[#3B4A63]" style={{ width: `${Math.max(6, Math.round((c.outstanding / top) * 100))}%` }} />
                      </div>
                      <span className="text-[10px] font-bold text-[#A0A3A8] w-24 text-right">
                        {c.count} inv{c.oldest_days > 0 ? ` · ${c.oldest_days}h` : ""}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Rekap pajak */}
      <Card className="p-6 border-0 rounded-3xl glass" data-testid="finance-tax-card">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-2xl bg-[#E4F9EC] text-[#3f7a52] flex items-center justify-center">
            <Percent className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div>
            <h3 className="text-[17px] font-bold text-[#090808]">Rekap Pajak {monthTitle}</h3>
            <span className="text-[11px] text-[#626061]/60 font-medium">Invoice terbit bulan ini</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TaxBlock testid="finance-tax-ppn" title="PPN 11%" accent="#3B4A63" s={data.tax_recap?.ppn} showPpn />
          <TaxBlock testid="finance-tax-nonppn" title="Non-PPN" accent="#626061" s={data.tax_recap?.non_ppn} />
        </div>
      </Card>
    </div>
  );
}

function OwnerFinanceBlocks({ ox, navigate }) {
  const dvc = (ox.deal_vs_cash || []).map((t) => ({
    month: MONTH_LABEL[Number((t.month || "").split("-")[1]) - 1] || t.month,
    deal: t.deal,
    diterima: t.collected,
  }));
  const gap = (ox.deal_total || 0) - (ox.collected_total || 0);
  const maxRecv = Math.max(1, ...(ox.receivable_by_sales || []).map((r) => r.outstanding));

  return (
    <div className="space-y-4" data-testid="owner-finance-blocks">
      <div className="flex items-center gap-3 pt-2">
        <span className="text-[11px] uppercase tracking-[0.24em] text-[#626061]/70 font-bold">Khusus Owner</span>
        <div className="flex-1 h-px bg-black/5" />
      </div>

      <Card className="p-6 border-0 rounded-3xl glass" data-testid="owner-deal-vs-cash">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
          <div>
            <h3 className="text-[17px] font-bold text-[#090808]">Deal vs Uang Masuk</h3>
            <p className="text-[11px] text-[#626061]/60 font-medium">Nilai proyek menang dibanding uang yang benar-benar diterima · 12 bulan</p>
          </div>
          <div className="flex items-center gap-6">
            <MiniStat label="Nilai Deal" value={fmtIDR(ox.deal_total)} />
            <MiniStat label="Uang Diterima" value={fmtIDR(ox.collected_total)} tone="#3f7a52" />
            <MiniStat label="Selisih" value={fmtIDR(gap)} tone={gap > 0 ? "#CE6968" : "#3f7a52"} />
          </div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={dvc} margin={{ top: 4, right: 8, left: 8, bottom: 0 }} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8A8D96" }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={compact} tick={{ fontSize: 10, fill: "#A0A3A8" }} axisLine={false} tickLine={false} width={54} />
            <Tooltip
              formatter={(v, n) => [fmtIDR(v), n]}
              contentStyle={{ borderRadius: 14, border: "1px solid rgba(0,0,0,0.06)", fontSize: 12 }}
            />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 11, paddingTop: 6 }} formatter={(value) => <span style={{ color: "#626061", fontWeight: 600 }}>{value}</span>} />
            <Bar dataKey="deal" name="Nilai Deal" fill="#FFDB52" radius={[6, 6, 0, 0]} maxBarSize={18} />
            <Bar dataKey="diterima" name="Uang Diterima" fill="#57D28A" radius={[6, 6, 0, 0]} maxBarSize={18} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-6 border-0 rounded-3xl glass" data-testid="owner-receivable-by-sales">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-2xl bg-[#E5EEFA] text-[#3B4A63] flex items-center justify-center">
              <Users className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div>
              <h3 className="text-[17px] font-bold text-[#090808]">Piutang per Sales</h3>
              <span className="text-[11px] text-[#626061]/60 font-medium">Siapa yang kliennya paling banyak nunggak</span>
            </div>
          </div>
          {(ox.receivable_by_sales || []).length === 0 ? (
            <div className="text-sm text-[#A0A3A8]">Tidak ada piutang berjalan.</div>
          ) : (
            <div className="space-y-3">
              {ox.receivable_by_sales.map((r) => (
                <div key={r.user_id} data-testid={`owner-recv-${r.user_id}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-xs font-bold text-[#090808] truncate">{r.nickname || r.name}</span>
                    <span className="text-xs font-num tabular-nums font-bold text-[#090808]">{fmtIDR(r.outstanding)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex-1 h-2.5 rounded-full bg-black/5 overflow-hidden flex">
                      <div className="h-full bg-[#CE6968]" style={{ width: `${Math.round((r.overdue / maxRecv) * 100)}%` }} />
                      <div className="h-full bg-[#97ADD4]" style={{ width: `${Math.round(((r.outstanding - r.overdue) / maxRecv) * 100)}%` }} />
                    </div>
                    <span className="text-[10px] font-bold text-[#A0A3A8] w-16 text-right">{r.count} inv</span>
                  </div>
                  {r.overdue > 0 && (
                    <div className="text-[10px] font-bold text-[#CE6968] mt-1">Telat {fmtIDR(r.overdue)}</div>
                  )}
                </div>
              ))}
              <div className="flex items-center gap-3 pt-1 text-[10px] font-bold text-[#A0A3A8]">
                <span className="inline-flex items-center gap-1.5"><i className="w-2 h-2 rounded-full bg-[#CE6968] inline-block" /> Lewat tempo</span>
                <span className="inline-flex items-center gap-1.5"><i className="w-2 h-2 rounded-full bg-[#97ADD4] inline-block" /> Belum tempo</span>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-6 border-0 rounded-3xl glass" data-testid="owner-unbilled">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-2xl bg-[#FFE89A] text-[#7A6633] flex items-center justify-center">
              <PiggyBank className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div>
              <h3 className="text-[17px] font-bold text-[#090808]">Belum Ditagih</h3>
              <span className="text-[11px] text-[#626061]/60 font-medium">
                {ox.unbilled_count || 0} proyek · potensi {fmtIDR(ox.unbilled_total)} tertahan
              </span>
            </div>
          </div>
          {(ox.unbilled || []).length === 0 ? (
            <div className="text-sm text-[#A0A3A8]">Semua proyek sudah ditagih penuh. 🎉</div>
          ) : (
            <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
              {ox.unbilled.map((u) => (
                <button
                  key={u.project_id}
                  onClick={() => navigate(`/projects?open=${u.project_id}`)}
                  data-testid={`owner-unbilled-${u.project_id}`}
                  className="w-full text-left bg-white/70 hover:bg-white rounded-2xl px-4 py-2.5 border border-white/70 transition-all flex items-center gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-[#090808] truncate">{u.name}</span>
                      {u.never_billed && (
                        <span className="shrink-0 inline-flex items-center h-5 px-2 rounded-full text-[10px] font-bold bg-[#FDE7E9] text-[#CE6968]">Belum ada invoice</span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#A0A3A8] font-semibold truncate">
                      {u.customer} · {u.sales_name} · kontrak {fmtIDR(u.total_value)}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-num font-bold text-[#7A6633] tabular-nums">{fmtIDR(u.unbilled)}</div>
                    <div className="text-[10px] text-[#A0A3A8] font-num tabular-nums">ditagih {fmtIDR(u.billed)}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#A0A3A8] shrink-0" />
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone = "#090808" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-[#8A8D96]">{label}</div>
      <div className="text-sm font-num tabular-nums font-extrabold mt-0.5" style={{ color: tone }}>{value}</div>
    </div>
  );
}

const KPI_TONES = {
  dark: "bg-[#090808] text-white",
  red: "bg-[#FDE7E9] border border-[#FFB3BF]",
  green: "bg-[#E4F9EC] border border-[#B7E7C7]",
  plain: "bg-white/70 border border-white/70",
};

function Kpi({ label, value, sub, icon: Icon, tone = "plain", testid, onClick }) {
  const dark = tone === "dark";
  const clickable = typeof onClick === "function";
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      className={`rounded-3xl p-5 transition-all ${KPI_TONES[tone]} ${clickable ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-16px_rgba(20,22,31,0.28)]" : ""}`}
    >
      <div className="flex items-center justify-between">
        <span className={`text-[10px] uppercase tracking-[0.14em] font-extrabold ${dark ? "text-[#B7BAC2]" : "text-[#8A8D96]"}`}>{label}</span>
        <Icon className={`w-4 h-4 ${dark ? "text-white/70" : "text-[#8A8D96]"}`} strokeWidth={1.75} />
      </div>
      <div className={`text-xl md:text-[26px] font-extrabold mt-3 font-num tabular-nums break-words leading-tight ${dark ? "text-white" : "text-[#090808]"}`}>
        {value}
      </div>
      <div className={`text-[11px] mt-1.5 font-semibold ${dark ? "text-[#8A8D96]" : "text-[#8A8D96]"}`}>{sub}</div>
    </div>
  );
}

function TaxBlock({ title, s, accent, showPpn, testid }) {
  const v = s || { count: 0, dpp: 0, ppn: 0, total: 0 };
  return (
    <div className="rounded-2xl bg-white/70 border border-white/70 p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-extrabold uppercase tracking-[0.12em]" style={{ color: accent }}>{title}</span>
        <span className="text-[10px] font-bold text-[#A0A3A8]">{v.count} invoice</span>
      </div>
      <div className="mt-3 space-y-1.5">
        <Row label="Dasar Pengenaan" value={fmtIDR(v.dpp)} />
        {showPpn && <Row label="PPN Terkumpul" value={fmtIDR(v.ppn)} />}
        <div className="pt-1.5 border-t border-black/5 flex items-baseline justify-between">
          <span className="text-[11px] font-bold text-[#626061]">Total Tagihan</span>
          <span className="text-sm font-num tabular-nums font-extrabold text-[#090808]">{fmtIDR(v.total)}</span>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-[11px] font-semibold text-[#A0A3A8]">{label}</span>
      <span className="text-xs font-num tabular-nums font-bold text-[#090808]">{value}</span>
    </div>
  );
}
